import {
  AllTradeMarkets,
  ColorMap,
  LegendLabels,
} from 'pages/Optimisation/components/ContractedVolume/types';
import {
  CHART_CONSTANTS,
  MARKET_FILTER_VALUES,
} from 'pages/Optimisation/constants';
import { AppThemeType } from 'store/types';

const { lightModeDisabledColor, darkModeDisabledColor, colorMap } =
  CHART_CONSTANTS;

// Derive groups from legend labels: identifiers that share the same groupKey are grouped together.
// This means adding a new grouped market only requires updating constants.ts.
export const buildMarketGroups = (
  labels: LegendLabels[MARKET_FILTER_VALUES],
): Record<string, string[]> => {
  const byGroupKey = new Map<string, string[]>();
  for (const { groupKey, identifier } of labels) {
    if (identifier === 'disabled' || !groupKey) continue;
    const existing = byGroupKey.get(groupKey) ?? [];
    existing.push(identifier);
    byGroupKey.set(groupKey, existing);
  }
  const groups: Record<string, string[]> = {};
  for (const members of byGroupKey.values()) {
    for (const id of members) {
      if (groups[id]) {
        throw new Error(
          `Duplicate market group member: "${id}" belongs to multiple groups`,
        );
      }
      groups[id] = members;
    }
  }
  return groups;
};

const MARKET_GROUPS = buildMarketGroups(
  CHART_CONSTANTS.legendLabels[MARKET_FILTER_VALUES.All],
);

export const getColourMap = (
  marketFilter: MARKET_FILTER_VALUES | AllTradeMarkets | 'disabled' | 'other',
  theme: AppThemeType,
) => {
  if (marketFilter === MARKET_FILTER_VALUES.All) {
    return colorMap;
  }
  const disabledColor =
    theme === 'light' ? lightModeDisabledColor : darkModeDisabledColor;

  const activeGroup = MARKET_GROUPS[marketFilter] ?? [marketFilter];

  // Create color map where all but the selected filter are disabled
  const newColorMap: ColorMap = Object.fromEntries(
    Object.entries(colorMap).map(([market, subMarkets]) => [
      market,
      // If the filter matches the entire market, use all original submarket colors
      marketFilter === market
        ? subMarkets
        : // Otherwise only keep the submarket colors that match the group, disable the rest
          Object.fromEntries(
            Object.entries(subMarkets).map(([subMarket, color]) => [
              subMarket,
              activeGroup.includes(subMarket) ? color : disabledColor,
            ]),
          ),
    ]),
  );

  return newColorMap;
};
